import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { AutoComplete, Button, Card, Col, DatePicker, Form, Input, InputNumber, message, Modal, Row, Select, Space, Statistic, Table, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { transportApi } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";
import { getEnteredByUser } from "../../utils/currentUser";
import { LookupSelect } from "../../shared/components/LookupSelect";

type ReceiptLine = { id?: number; documentType: string; numbersPerBook: number; startNumber: number; endNumber?: number };
type Receipt = { id: number; printerCompanyName: string; printerContact?: string; printerAddress?: string; invoiceNumber: string; invoiceDate: string; invoiceAmount: number; lines: ReceiptLine[] };
type Printer = { id: number; companyName: string; contactDetails?: string; address?: string };
type InventoryLine = ReceiptLine & { id: number; receiptId: number; printerCompanyName: string; invoiceNumber: string; invoiceDate: string; assignment?: { id: number; assignedBranchId: number; branchName: string; issueDate: string; isActive: boolean; state: string } | null };

export function HeadOfficeStationeryReceiptPanel() {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [inventoryLines, setInventoryLines] = useState<InventoryLine[]>([]);
  const [selectedLineIds, setSelectedLineIds] = useState<number[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignForm] = Form.useForm();
  const watchedLines = Form.useWatch("lines", form) as ReceiptLine[] | undefined;

  const load = async () => {
    setLoading(true);
    try {
      const [printerMasters, lines] = await Promise.all([transportApi.listStationeryPrinters(), transportApi.listHeadOfficeStationeryInventoryLines()]);
      setPrinters(printerMasters); setInventoryLines(lines); setSelectedLineIds([]);
    }
    catch (error) { message.error(getApiErrorMessage(error, "Unable to load Head Office receipts")); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const totals = useMemo(() => (watchedLines ?? []).reduce<Record<string, number>>((result, line) => {
    if (line?.documentType) result[line.documentType] = (result[line.documentType] ?? 0) + (line.numbersPerBook || 50);
    return result;
  }, {}), [watchedLines]);

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await transportApi.createHeadOfficeStationeryReceipt({ ...values, invoiceDate: values.invoiceDate.toISOString(), enteredBy: getEnteredByUser() });
      message.success("Printer stationery received into Head Office inventory");
      setOpen(false); form.resetFields(); await load();
    } catch (error) { message.error(getApiErrorMessage(error, "Unable to receive stationery")); }
    finally { setSaving(false); }
  };

  const assign = async () => {
    const values = await assignForm.validateFields();
    setAssigning(true);
    try {
      await transportApi.assignHeadOfficeStationeryToBranch({ ...values, receiptLineIds: selectedLineIds });
      message.success(`${selectedLineIds.length} book(s) assigned to the selected branch`);
      setAssignOpen(false); assignForm.resetFields(); await load();
    } catch (error) { message.error(getApiErrorMessage(error, "Unable to assign stationery")); }
    finally { setAssigning(false); }
  };

  return <>
    <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
      <div><Typography.Title level={5} style={{ margin: 0 }}>HO Printer Inventory &amp; Branch Assignment</Typography.Title><Typography.Text type="secondary">Receive physical books, then select available books and assign them to one branch.</Typography.Text></div>
      <Space><Button type="primary" disabled={selectedLineIds.length === 0} onClick={() => setAssignOpen(true)}>Assign {selectedLineIds.length || "Selected"} to Branch</Button><Button type="primary" style={{ background: "#389e0d", borderColor: "#389e0d" }} icon={<PlusOutlined />} onClick={() => { form.setFieldsValue({ invoiceDate: dayjs(), lines: [{ documentType: "Consignment", numbersPerBook: 50 }] }); setOpen(true); }}>Receive from Printer</Button></Space>
    </Space>
    <Table<InventoryLine> rowKey="id" loading={loading} dataSource={inventoryLines} rowSelection={{ selectedRowKeys: selectedLineIds, onChange: (keys) => setSelectedLineIds(keys.map(Number)), getCheckboxProps: (line) => ({ disabled: !!line.assignment }) }} columns={[
      { title: "Document Type", dataIndex: "documentType" }, { title: "Range", render: (_, line) => `${line.startNumber}–${line.endNumber}` },
      { title: "Per Book", dataIndex: "numbersPerBook", width: 90 }, { title: "Printer", dataIndex: "printerCompanyName" },
      { title: "Invoice", dataIndex: "invoiceNumber" }, { title: "Received", dataIndex: "invoiceDate", render: (value) => dayjs(value).format("DD/MM/YYYY") },
      { title: "Status", render: (_, line) => line.assignment ? <Typography.Text type="secondary">Assigned to {line.assignment.branchName}</Typography.Text> : <Typography.Text style={{ color: "#389e0d" }}>Available at HO</Typography.Text> }
    ]} pagination={{ pageSize: 25 }} />

    <Modal open={assignOpen} title="Assign Selected HO Inventory to Branch" onCancel={() => setAssignOpen(false)} onOk={() => void assign()} confirmLoading={assigning} okText="Assign Books" destroyOnHidden>
      <Typography.Paragraph>{selectedLineIds.length} physical book(s) selected. A book cannot be assigned to another branch later.</Typography.Paragraph>
      <Form form={assignForm} layout="vertical">
        <Form.Item name="branchId" label="Destination Branch / Station" rules={[{ required: true }]}><LookupSelect fetcher={(q, page, pageSize) => transportApi.searchBranchLookups(q, page, pageSize)} placeholder="Select branch / station" /></Form.Item>
        <Form.Item name="challanNo" label="Issue Challan / Reference"><Input /></Form.Item>
        <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
      </Form>
    </Modal>

    <Modal open={open} title="Issue to HO — Receive from Offset Printer" width={1000} onCancel={() => setOpen(false)} onOk={() => void save()} confirmLoading={saving} okText="Receive into HO Inventory" destroyOnHidden>
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Form.Item name="printerId" hidden><Input /></Form.Item>
          <Col span={8}><Form.Item name="printerCompanyName" label="Printer Company Name" rules={[{ required: true }]} extra="Select an existing printer or type a new company name.">
            <AutoComplete
              options={printers.map((printer) => ({ value: printer.companyName, label: printer.companyName, printer }))}
              filterOption={(input, option) => String(option?.value ?? "").toLowerCase().includes(input.toLowerCase())}
              onSelect={(_, option: any) => {
                const printer = option.printer as Printer;
                form.setFieldsValue({ printerId: printer.id, printerCompanyName: printer.companyName, printerContact: printer.contactDetails, printerAddress: printer.address });
              }}
              onChange={(value) => {
                const match = printers.find((printer) => printer.companyName.toLowerCase() === value.trim().toLowerCase());
                if (!match) form.setFieldValue("printerId", undefined);
              }}
              placeholder="Select or enter printer company"
            />
          </Form.Item></Col>
          <Col span={8}><Form.Item name="printerContact" label="Contact Details"><Input /></Form.Item></Col>
          <Col span={8}><Form.Item name="printerAddress" label="Address"><Input /></Form.Item></Col>
          <Col span={8}><Form.Item name="invoiceNumber" label="Printer Invoice Number" rules={[{ required: true }]}><Input /></Form.Item></Col>
          <Col span={8}><Form.Item name="invoiceDate" label="Invoice Date" rules={[{ required: true }]}><DatePicker style={{ width: "100%" }} /></Form.Item></Col>
          <Col span={8}><Form.Item name="invoiceAmount" label="Invoice Amount" rules={[{ required: true }]}><InputNumber min={0} prefix="₹" style={{ width: "100%" }} /></Form.Item></Col>
        </Row>
        <Form.List name="lines">
          {(fields, { add, remove }) => <>
            <table className="ho-receipt-entry-table"><thead><tr><th>SN</th><th>Document Type</th><th>Per Book</th><th>Start Number</th><th>End Number</th><th /></tr></thead>
              <tbody>{fields.map((field, index) => {
                const line = watchedLines?.[index]; const end = line?.startNumber ? Number(line.startNumber) + 49 : undefined;
                return <tr key={field.key}><td>{index + 1}</td><td><Form.Item name={[field.name, "documentType"]} rules={[{ required: true }]}><Select options={[
                  { value: "Consignment", label: "Consignment" }, { value: "Manifest", label: "Manifest" }, { value: "GatePass", label: "Gate Pass" }
                ]} /></Form.Item></td>
                  <td><Form.Item name={[field.name, "numbersPerBook"]}><InputNumber value={50} disabled /></Form.Item></td>
                  <td><Form.Item name={[field.name, "startNumber"]} rules={[{ required: true }]}><InputNumber min={1} style={{ width: "100%" }} /></Form.Item></td>
                  <td><InputNumber value={end} disabled style={{ width: "100%" }} /></td><td><Button danger type="text" icon={<DeleteOutlined />} disabled={fields.length === 1} onClick={() => remove(field.name)} /></td></tr>;
              })}</tbody>
            </table>
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => { const lines = form.getFieldValue("lines") ?? []; const previous = lines[lines.length - 1]; add({ documentType: previous?.documentType ?? "Consignment", numbersPerBook: 50, startNumber: previous?.startNumber ? Number(previous.startNumber) + 50 : undefined }); }} block>Add another 50-number book</Button>
          </>}
        </Form.List>
        <Card size="small" style={{ marginTop: 12 }}><Row gutter={16}>{Object.entries(totals).map(([type, total]) => <Col key={type}><Statistic title={type} value={total} suffix="numbers" /></Col>)}</Row></Card>
      </Form>
    </Modal>
  </>;
}
